"use client";

import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import {
  ArrowLeftIcon,
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  ShareIcon,
  HeartIcon,
  TicketIcon,
  BuildingStorefrontIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/outline";
import { HeartIcon as HeartSolidIcon } from "@heroicons/react/24/solid";
import type { RemoteEvent } from "@/types";

type EventDesktopViewProps = {
  event: RemoteEvent;
  imageUrl: string;
  dateInfo: {
    dateLabel: string;
    hourLabel: string;
    fullLabel: string;
  };
  location: string;
  address: string | null;
};

export default function EventDesktopView({
  event,
  imageUrl,
  dateInfo,
  location,
  address,
}: EventDesktopViewProps) {
  const [isFavorite, setIsFavorite] = useState(false);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: event.title,
          text:
            event.description || `Confira ${event.title} no Santa Fé Conecta`,
          url: window.location.href,
        });
      } catch {
        // User cancelled
      }
    }
  };

  // Parse date for badge - dateLabel format: "27 de nov" or "27 nov"
  const dateParts = dateInfo.dateLabel.split(" ");
  const day = dateParts[0] || "";
  // Se tem "de" no meio (ex: "27 de nov"), pega o último elemento, senão pega o segundo
  const month = dateParts.length === 3 ? dateParts[2] : dateParts[1] || "";

  return (
    <div className="hidden md:block bg-gray-50">
      {/* Hero Banner */}
      <div className="relative h-96 w-full overflow-hidden">
        <Image
          src={imageUrl}
          alt={event.title}
          fill
          className="object-cover"
          priority
          unoptimized
        />
        <div className="absolute inset-0 bg-linear-to-b from-black/60 via-black/40 to-gray-50" />

        {/* Top Navigation */}
        <div className="absolute top-0 left-0 right-0 z-20">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <Link
              href="/eventos"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 backdrop-blur-md text-white hover:bg-white/20 transition-all font-medium"
            >
              <ArrowLeftIcon className="w-5 h-5" />
              Voltar para Eventos
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 -mt-40 relative z-10 pb-16">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Event Card */}
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
              {/* Image with Date Badge */}
              <div className="relative h-80 w-full overflow-hidden">
                <Image
                  src={imageUrl}
                  alt={event.title}
                  fill
                  className="object-cover"
                  priority
                  unoptimized
                />
                <div className="absolute inset-0 bg-linear-to-t from-black/60 via-transparent to-transparent" />

                {/* Date Badge */}
                <div className="absolute bottom-6 left-6">
                  <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
                    <div className="px-6 py-4 text-center">
                      <div className="text-4xl font-bold text-gray-900">
                        {day}
                      </div>
                      <div className="text-sm font-semibold text-mygreen uppercase tracking-wider">
                        {month}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Status Badge */}
                <div className="absolute top-6 left-6">
                  <div className="inline-flex items-center gap-1.5 px-4 py-2 bg-mygreen text-white rounded-full text-sm font-semibold shadow-lg">
                    <TicketIcon className="w-4 h-4" />
                    Evento Confirmado
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="absolute top-6 right-6 flex items-center gap-2">
                  <button
                    onClick={() => setIsFavorite(!isFavorite)}
                    className="flex items-center justify-center w-12 h-12 rounded-xl bg-white/90 backdrop-blur-md text-gray-600 hover:bg-white hover:text-red-500 transition-all shadow-lg"
                  >
                    {isFavorite ? (
                      <HeartSolidIcon className="w-6 h-6 text-red-500" />
                    ) : (
                      <HeartIcon className="w-6 h-6" />
                    )}
                  </button>
                  <button
                    onClick={handleShare}
                    className="flex items-center justify-center w-12 h-12 rounded-xl bg-white/90 backdrop-blur-md text-gray-600 hover:bg-white hover:text-mygreen transition-all shadow-lg"
                  >
                    <ShareIcon className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-4">
                  {event.title}
                </h1>

                {/* Description */}
                {event.description && (
                  <div className="mb-6">
                    <div
                      className="text-gray-600 leading-relaxed text-lg whitespace-pre-line space-y-4 style-from-html"
                      dangerouslySetInnerHTML={{ __html: event.description }}
                    />
                  </div>
                )}

                {/* Company Info */}
                {event.company && event.company.name && (
                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-2xl">
                    <div className="w-14 h-14 rounded-xl bg-white shadow-sm flex items-center justify-center">
                      <BuildingStorefrontIcon className="w-7 h-7 text-gray-500" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-500 font-medium">
                        Organização
                      </p>
                      <p className="font-semibold text-gray-900 text-lg">
                        {event.company.name}
                      </p>
                    </div>
                    {event.company.city && (
                      <div className="text-right">
                        <p className="text-sm text-gray-500 font-medium">
                          Cidade
                        </p>
                        <p className="font-medium text-gray-900">
                          {event.company.city}
                          {event.company.state
                            ? ` - ${event.company.state}`
                            : ""}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* CTA Buttons */}
              <div className="px-8 pb-8">
                <div className="flex flex-wrap gap-3">
                  <Link
                    href="/eventos"
                    className="inline-flex items-center gap-2 px-8 py-4 bg-mygreen text-white rounded-xl font-semibold hover:bg-mygreen/90 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                  >
                    <CalendarIcon className="w-6 h-6" />
                    Ver Outros Eventos
                  </Link>
                  <Link
                    href="/contato"
                    className="inline-flex items-center gap-2 px-8 py-4 border-2 border-mygreen text-mygreen rounded-xl font-semibold hover:bg-mygreen/10 transition-all"
                  >
                    <ArrowTopRightOnSquareIcon className="w-5 h-5" />
                    Quero Divulgar Meu Evento
                  </Link>
                  <button
                    onClick={handleShare}
                    className="inline-flex items-center gap-2 px-6 py-4 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all"
                  >
                    <ShareIcon className="w-5 h-5" />
                    Compartilhar
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Event Details Card */}
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden sticky top-6">
              {/* Header */}
              <div className="bg-linear-to-r from-mygreen to-mygreen/80 px-6 py-5">
                <h2 className="text-white font-bold text-lg flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5" />
                  Informações do Evento
                </h2>
              </div>

              {/* Info Items */}
              <div className="divide-y divide-gray-100">
                {/* Full Date */}
                <div className="flex items-center gap-4 p-5">
                  <div className="w-12 h-12 rounded-xl bg-mygreen/10 text-mygreen flex items-center justify-center shrink-0">
                    <CalendarIcon className="w-6 h-6" />
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 uppercase tracking-wider font-medium block">
                      Data
                    </span>
                    <span className="text-gray-900 font-semibold capitalize">
                      {dateInfo.fullLabel}
                    </span>
                  </div>
                </div>

                {/* Time */}
                <div className="flex items-center gap-4 p-5">
                  <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                    <ClockIcon className="w-6 h-6" />
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 uppercase tracking-wider font-medium block">
                      Horário
                    </span>
                    <span className="text-gray-900 font-semibold">
                      {dateInfo.hourLabel}
                    </span>
                  </div>
                </div>

                {/* Location */}
                <div className="flex items-center gap-4 p-5">
                  <div className="w-12 h-12 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
                    <MapPinIcon className="w-6 h-6" />
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 uppercase tracking-wider font-medium block">
                      Local
                    </span>
                    <span className="text-gray-900 font-semibold">
                      {location}
                    </span>
                  </div>
                </div>

                {/* Address */}
                {address && (
                  <div className="flex items-start gap-4 p-5">
                    <div className="w-12 h-12 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center shrink-0">
                      <BuildingStorefrontIcon className="w-6 h-6" />
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 uppercase tracking-wider font-medium block">
                        Endereço
                      </span>
                      <span className="text-gray-900 font-medium leading-relaxed">
                        {address}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Map */}
              {address && (
                <>
                  <div className="w-full aspect-video relative">
                    <iframe
                      className="w-full h-full"
                      style={{ border: 0 }}
                      loading="lazy"
                      allowFullScreen
                      referrerPolicy="no-referrer-when-downgrade"
                      src={`https://www.google.com/maps/embed/v1/place?key=${
                        process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY ||
                        "AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8"
                      }&q=${encodeURIComponent(address)}`}
                    />
                  </div>
                  <div className="p-4">
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                        address
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full bg-mygreen text-white py-3 rounded-xl font-semibold hover:bg-mygreen/90 transition-colors"
                    >
                      <MapPinIcon className="w-5 h-5" />
                      Como Chegar
                    </a>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
